import React, { Component } from 'react';
import { getClearButtonWrapper, parseRenderOptions } from './utils';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import { Icon } from '../Icon';

/******************************************************************************
 * Single Field
 ******************************************************************************/

const TextComponent = ({ renderOptions, ...props }) => {
  // Get wrapper information if clear button can be visible
  const {
    Tag,
    props: tagProps,
    content,
  } = getClearButtonWrapper(renderOptions, props);

  return (
    <FieldWrapper {...props} renderOptions={renderOptions}>
      <Tag {...tagProps}>
        <input
          ref={props.focusRef}
          className={classNames('form-control', renderOptions.fieldClassName, {
            'form-control-sm': renderOptions.size === 'sm',
            'is-invalid': renderOptions.hasErrors,
            'has-btn': renderOptions.hasClear && !!props.value,
            'is-empty': !props.value,
          })}
          type="text"
          id={props.id}
          name={props.name}
          value={props.value}
          onBlur={props.onBlur}
          onChange={props.onChange}
          onFocus={props.onFocus}
          placeholder={renderOptions.placeholder}
          disabled={!props.enabled}
          form={props.form}
        />
        {content}
      </Tag>
    </FieldWrapper>
  );
};

export const TextField = props => {
  const renderOptions = parseRenderOptions(props);
  return <TextComponent renderOptions={renderOptions} {...props} />;
};

export const TextFilter = props => {
  const renderOptions = parseRenderOptions(props, null, {
    clear: true,
    floating: true,
    size: 'sm',
  });
  return <TextComponent renderOptions={renderOptions} {...props} />;
};

export const TextAreaField = props => {
  const renderOptions = parseRenderOptions(props);
  // Get wrapper information if clear button can be visible
  const {
    Tag,
    props: tagProps,
    content,
  } = getClearButtonWrapper(renderOptions, props);
  return (
    <FieldWrapper {...props} renderOptions={renderOptions}>
      <Tag {...tagProps}>
        <textarea
          ref={props.focusRef}
          rows={
            renderOptions.custom.rows || (renderOptions.size === 'sm' ? 3 : 5)
          }
          cols={props.cols}
          className={classNames('form-control', renderOptions.fieldClassName, {
            'form-control-sm': renderOptions.size === 'sm',
            'is-invalid': renderOptions.hasErrors,
            'has-btn': renderOptions.hasClear && !!props.value,
            'is-empty': !props.value,
          })}
          id={props.id || props.name}
          name={props.name}
          value={props.value || ''}
          onBlur={props.onBlur}
          onChange={props.onChange}
          onFocus={props.onFocus}
          placeholder={renderOptions.placeholder}
          disabled={!props.enabled}
          form={props.form}
        />
        {content}
      </Tag>
    </FieldWrapper>
  );
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export class TextMultiComponent extends Component {
  onEdit = index => event => {
    this.props.onChange(
      event.target.value
        ? this.props.value.set(index, event.target.value)
        : this.props.value.delete(index),
    );
  };

  onAdd = event => {
    this.props.onChange(this.props.value.push(event.target.value));
  };

  onRemove = index => () => {
    this.props.onChange(this.props.value.delete(index));
  };

  // When rendering the inputs we append an empty string to the list of values,
  // this is helpful because then the "new" input is in the keyed collection so
  // when text is entered there we get a smooth addition of another new input.
  render() {
    const { renderOptions, ...props } = this.props;

    return (
      <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
        {props.value
          // If the field is enabled or has no values, append an empty string
          // for adding another value to the values list
          .concat(props.enabled || props.value.size === 0 ? [''] : [])
          .map((selection, i) => (
            <div
              className={classNames('input-group', {
                'input-group-sm': renderOptions.size === 'sm',
              })}
              key={i}
            >
              {renderOptions.custom.sequence && (
                <span className="input-group-text">
                  {typeof renderOptions.custom.sequence === 'function'
                    ? renderOptions.custom.sequence(i)
                    : i}
                </span>
              )}
              <div className="field-floating">
                <input
                  type="text"
                  id={props.id}
                  className={classNames('form-control', {
                    'form-control-sm': renderOptions.size === 'sm',
                    'is-invalid': !selection && renderOptions.hasErrors,
                    'has-btn': !!selection,
                    'is-empty': !selection,
                  })}
                  disabled={!props.enabled}
                  onBlur={props.onBlur}
                  onChange={selection ? this.onEdit(i) : this.onAdd}
                  onFocus={props.onFocus}
                  placeholder={
                    !selection ? renderOptions.placeholder : undefined
                  }
                  value={selection}
                  form={props.form}
                />
                {selection && props.enabled && (
                  <button
                    type="button"
                    className={classNames('btn btn-link-primary btn-floating', {
                      'btn-sm': renderOptions.size === 'sm',
                    })}
                    onClick={this.onRemove(i)}
                    aria-label="Clear Value"
                    title="Clear Value"
                  >
                    <Icon name="x" md />
                  </button>
                )}
              </div>
            </div>
          ))}
      </FieldWrapper>
    );
  }
}

export const TextMultiField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  return <TextMultiComponent renderOptions={renderOptions} {...props} />;
};

export const TextMultiFilter = props => {
  const renderOptions = parseRenderOptions(
    props,
    { isFloating: false },
    { size: 'sm' },
  );
  return <TextMultiComponent renderOptions={renderOptions} {...props} />;
};
